import { useState, useContext, useEffect } from 'react';
import { FluidForm } from '../../../components/forms';
import { TimerEntity, ResourceEntity } from '../../../types/game-document/';
import { Button } from '@progress/kendo-react-buttons';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import { GameDocumentContext } from '../../../contexts/game-document';

import { ResourceType } from '../../../types/game-document/resource-type';
import { uuid } from '../../../types/common-helper';
import {
  ERR_DUPLICATE_NAME_VALUE,
  ERR_INPUT_REQUIRED
} from '../../../constants/text';
import RequiredFields from '../../../types/required-fields';
import { TimerEditorFull } from '../../../features/game-document/timers/timer-editor-full';
import {
  SaveTimerEntityEditorAsync,
  UpdateGameDocState
} from '../../../utils/game-document';
import Toolbar from '../toolbar';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../../../contexts/breadcrumb';

export const TimerNew = () => {
  const titleGuid = uuid();

  const newEntity: TimerEntity = {
    id: '',
    name: '',
    description: '',
    titleResId: titleGuid,
    direction: 'down',
    repeats: false
  };

  const titleResource: ResourceEntity = {
    id: titleGuid,
    name: 'Title',
    description: '',
    type: 'text',
    value: ''
  };
  const [state, setState] = useContext(GameDocumentContext);
  const [entity, setEntity] = useState<TimerEntity>(newEntity);
  const [resources, setResources] = useState<ResourceEntity[]>([titleResource]);

  const navigate = useNavigate();

  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<TimerEntity>[]
  >([{ name: 'titleResId', errorMessage: '' }]);

  const updateEntityAsync = async (
    name: keyof TimerEntity,
    value: string | number,
    type: ResourceType,
    isClearValue?: boolean
  ) => {
    if (!name?.toLowerCase().includes('resid')) {
      setEntity((prev) => ({ ...prev, [name]: isClearValue ? '' : value }));
    } else {
      const resourceId = entity[name];
      let newResources: ResourceEntity[] = [...resources];
      if (resourceId === undefined || resourceId === '') {
        const newId: string = uuid();
        let resourceEntity: ResourceEntity = {
          id: newId,
          description: '',
          name: name,
          type: type,
          value: value as string
        };
        newResources.push(resourceEntity);
        setResources(newResources);
        setEntity((prev) => ({ ...prev, [name]: newId }));
      } else {
        setResources(
          newResources.map((res) =>
            res?.id === resourceId
              ? { ...res, value: isClearValue ? '' : (value as string) }
              : { ...res }
          )
        );
        setEntity((prev) => ({ ...prev, [name]: resourceId }));
      }
    }
  };

  const isInputValid = (): boolean => {
    const inputValidation = requiredFields.map((input) => {
      const entityValue = entity[input.name as keyof TimerEntity];

      if (input.name === 'titleResId') {
        // Check validation for input titleResId
        const resourceIndex = resources.findIndex(
          (resource) => resource?.id === entityValue
        );
        if (
          resources[resourceIndex] === undefined ||
          resources[resourceIndex]?.value === ''
        ) {
          input.errorMessage = ERR_INPUT_REQUIRED;
        } else {
          input.errorMessage = '';
        }
      }

      return input;
    });
    setRequiredFields(inputValidation);
    return (
      inputValidation.findIndex((input) => input.errorMessage !== '') === -1
    );
  };

  const handleEntityEditorSubmit = (
    editorEntity: EntityEditor<TimerEntity>,
    resourceEntity: EntityEditor<ResourceEntity>[]
  ) => {
    SaveTimerEntityEditorAsync(
      state?.gameDocument!,
      editorEntity,
      resourceEntity
    ).then((response) => {
      if (response) {
        setState((prev) => UpdateGameDocState(prev, response));
        navigate(-1);
      }
    });
  };

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/timers/*', text: 'Timers' },
      {
        path: '/designer/:gameId/assets/timers/:timerId/*',
        text: ''
      }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar title={'New Timer'} showDefaultContent={false}>
        <div className={'d-flex ml-2 gap-2 pl-3'}>
          <Button
            themeColor={'secondary'}
            onClick={() => {
              navigate(-1);
            }}>
            Back
          </Button>
          <Button
            themeColor={'primary'}
            onClick={() => {
              if (isInputValid()) {
                const entityResources = resources.map((item, index) => ({
                  isNew: true,
                  entity: item
                })) as EntityEditor<ResourceEntity>[];
                handleEntityEditorSubmit(
                  { isNew: true, entity },
                  entityResources
                );
              }
            }}>
            Create
          </Button>
        </div>
      </Toolbar>

      <hr />
      <TimerEditorFull
        timer={entity}
        resources={resources}
        requiredFields={requiredFields}
        handleEntityChange={(
          name: keyof TimerEntity,
          value: string | number,
          type: ResourceType,
          isClearValue?: boolean
        ) => {
          updateEntityAsync(name, value, type, isClearValue);
        }}
        handleEventAction={(entity) => setEntity(entity)}
      />
    </>
  );
};
